<template>
  <section v-if="featureEnabled" class="alt" data-testid="account-languages-table">
    <!-- Card header (ER-11416 design): title + explanatory copy + divider. -->
    <header class="alt__header">
      <h2 class="alt__title">{{ headerTitle }}</h2>
      <p v-if="headerSubtitle" class="alt__subtitle">{{ headerSubtitle }}</p>
      <div class="alt__divider" role="presentation" />
    </header>

    <!-- Filters bar above the table. The parent owns the data; this component
         only forwards filter intent through events. -->
    <div class="alt__toolbar">
      <SearchBar
        class="alt__search"
        label="More filters"
        ghost-text="Search"
        :length="appliedFilterChips.length"
        :add="true"
        :filter-btn="true"
        btn-text="Add language"
        action-button-icon="plusicon.svg"
        :chip-text="appliedFilterChips"
        :value-search="filters.search || ''"
        :counter-section="false"
        data-testid="language-search"
        @onSearch="onSearch"
        @onClick="onToolbarClick"
        @removeFilter="onRemoveFilter"
      />
    </div>

    <div class="alt__body">
      <!-- Loading skeleton for the table body -->
      <div v-if="loading" class="alt__loading" role="status" aria-live="polite">
        <span class="alt__visually-hidden">Loading languages</span>
        <div v-for="n in 3" :key="n" class="alt__skeleton-row" aria-hidden="true">
          <span class="alt__skeleton-bar alt__skeleton-bar--wide" />
          <span class="alt__skeleton-bar alt__skeleton-bar--badge" />
          <span class="alt__skeleton-bar alt__skeleton-bar--short" />
          <span class="alt__skeleton-bar alt__skeleton-bar--icon" />
          <span class="alt__skeleton-bar alt__skeleton-bar--icon" />
        </div>
      </div>

      <table v-else-if="rows.length" class="alt__table" :aria-busy="loading">
        <caption class="alt__visually-hidden">
          Account languages
        </caption>
        <colgroup>
          <col class="alt__col-language" />
          <col class="alt__col-status" />
          <col class="alt__col-icon" />
          <col class="alt__col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="alt__th">Language</th>
            <th scope="col" class="alt__th alt__th--center">Status</th>
            <th scope="col" class="alt__th alt__th--center">
              <span class="alt__visually-hidden">Chatter</span>
            </th>
            <th scope="col" class="alt__th alt__th--center">
              <span class="alt__visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="rowKey(row, index)"
            class="alt__row"
            :class="{ 'alt__row--retired': row.retired }"
          >
            <td class="alt__cell alt__cell--language">
              <div class="alt__language">
                <span class="alt__name" :title="row.name">{{ row.name }}</span>
                <span
                  v-if="row.isDefault"
                  class="alt__badge alt__badge--default"
                  data-testid="default-badge"
                >
                  Default
                </span>
                <span
                  v-if="row.dubbing"
                  class="alt__badge alt__badge--dubbing"
                  data-testid="dubbing-badge"
                >
                  Dubbing
                </span>
              </div>
            </td>

            <td class="alt__cell alt__cell--status">
              <OrgObjectStatusBadge
                :status="row.status"
                :neutral="row.retired"
                data-testid="language-status"
              />
            </td>

            <td class="alt__cell alt__cell--icon">
              <button
                type="button"
                class="alt__icon-btn"
                :aria-label="`Open chatter for ${row.name}`"
                data-testid="row-chatter"
                @click="onRowChatter(row)"
              >
                <ChatterIcon :unread="row.unread" />
              </button>
            </td>

            <td class="alt__cell alt__cell--actions">
              <MenuIcon
                v-if="row.menuItems.length"
                :dropdownItems="row.menuItems"
                :menuWidth="true"
                :showTooltip="false"
                data-testid="row-actions-menu"
                @onClick="onMenuChoice(row, $event)"
              >
                <template #activator>
                  <button
                    type="button"
                    class="alt__icon-btn"
                    :aria-label="`Actions for ${row.name}`"
                    aria-haspopup="menu"
                    data-testid="row-actions"
                  >
                    <img
                      :src="dotsHorizontalIconSrc"
                      alt=""
                      width="24"
                      height="24"
                      aria-hidden="true"
                    />
                  </button>
                </template>
              </MenuIcon>
              <span v-else class="alt__no-actions" aria-hidden="true" />
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="alt__empty" role="status">
        <p class="alt__empty__title">No languages found</p>
        <p class="alt__empty__subtitle">Try changing the filters or clearing your search.</p>
      </div>
    </div>

    <ExPagination
      v-if="pagination.pageCount > 1"
      class="alt__pagination"
      :pagination="paginationForEx"
      @onRequest="onPaginationRequest"
    />
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import SearchBar from "@components/SearchBar/SearchBar.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import { LD_FLAG_IDEA_MDP_595 } from "@components/constants/launchDarklyFlagKeys";
import { buildRowView } from "./accountLanguagesTableRow.js";

const dotsHorizontalIconSrc = require("@components/assets/job-family/dots-horizontal.svg");

/**
 * ER-11416 — AccountLanguagesTable (account Languages settings screen).
 *
 * Presentational composition of existing shared components (SearchBar,
 * ChatterIcon, ExPagination) for the list of front-end languages configured for
 * an account. The parent owns data fetching, filtering/pagination state and any
 * backend mutations; this component only re-emits intent through
 * `filter-change` (search), `more-filters`, `remove-filter`, `add-language`,
 * `row-chatter`, `row-action` and `page-change`.
 *
 * Row states (ER-11416 / ER-11373 AC):
 *   - Active  — the row carries an Actions overflow menu (dots-horizontal).
 *   - Retired — muted name, neutral status pill and an Actions overflow menu
 *               offering "Reactivate" (Rule 4 / Rule 13); the discussion thread
 *               stays available via chatter.
 *
 * Shipped behind the linked idea flag (idea-MDP-595, "Operating Languages —
 * account-level UI language configuration"). Kill-switch semantics: renders by
 * default; set the flag to false to hide the new table until the Languages
 * settings screen wires it.
 */
const props = defineProps({
  /** Account operating-language rows. See accountLanguagesTableRow.js for the shape. */
  languages: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    default: () => ({ status: [], search: "" }),
  },
  /** Labels of the applied filters, rendered as removable chips in the filters bar. */
  appliedFilterChips: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({ page: 1, pageCount: 1 }),
  },
  headerTitle: {
    type: String,
    default: "Languages used in this account",
  },
  headerSubtitle: {
    type: String,
    default:
      "Front-end languages configured for this account. Active languages are in use; retired languages are kept as read-only history.",
  },
});

const emit = defineEmits([
  "filter-change",
  "more-filters",
  "remove-filter",
  "add-language",
  "row-chatter",
  "row-action",
  "page-change",
]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_595, true)) !== false;
  } catch {
    // Kill-switch lookup failures never hide the table.
    featureEnabled.value = true;
  }
});

const rows = computed(() => props.languages.map((language) => buildRowView(language)));

const paginationForEx = computed(() => {
  const pageCount = Number(props.pagination.pageCount) || 1;
  const requestedPage = Number(props.pagination.page) || 1;
  return {
    page: Math.min(Math.max(requestedPage, 1), pageCount),
    total: pageCount,
    perPage: 1,
  };
});

function rowKey(row, index) {
  return row.id !== null && row.id !== undefined ? String(row.id) : `${row.status}-${index}`;
}

function onSearch(searchText) {
  emit("filter-change", { ...props.filters, search: searchText });
}

function onRowChatter(row) {
  emit("row-chatter", row.id);
}

function onToolbarClick(payload) {
  if (payload === "add") {
    emit("add-language");
    return;
  }
  emit("more-filters");
}

function onRemoveFilter(chipLabel) {
  emit("remove-filter", chipLabel);
}

function onMenuChoice(row, item) {
  if (item && item.value) {
    emit("row-action", { id: row.id, action: item.value });
  }
}

function onPaginationRequest({ page }) {
  emit("page-change", page);
}
</script>

<style scoped>
.alt {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.alt__header {
  padding: 24px 24px 0;
}

.alt__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.alt__subtitle {
  margin: 4px 0 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.alt__divider {
  height: 1px;
  margin-top: 20px;
  background: #eaecf0;
}

.alt__toolbar {
  display: flex;
  align-items: center;
  padding: 16px 24px 20px;
}

.alt__search {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.alt__body {
  width: 100%;
  overflow-x: auto;
}

.alt__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.alt__col-language {
  width: 56%;
}

.alt__col-status {
  width: 18%;
}

.alt__col-icon {
  width: 12%;
}

.alt__col-actions {
  width: 14%;
}

.alt__table thead th {
  height: 44px;
  padding: 12px 24px;
  box-sizing: border-box;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.alt__table tbody td {
  height: 72px;
  padding: 16px 24px;
  box-sizing: border-box;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.alt__table tbody tr:last-child td {
  border-bottom: none;
}

.alt__row--retired .alt__cell--status {
  background: #fcfcfd;
}

.alt__th--center {
  text-align: center;
}

.alt__cell--status,
.alt__cell--icon,
.alt__cell--actions {
  text-align: center;
}

.alt__language {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.alt__name {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alt__row--retired .alt__name {
  color: #98a2b0;
}

.alt__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 16px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  flex-shrink: 0;
}

.alt__badge--default {
  border: 1px solid #d0d5dd;
  background: #f9fafb;
  color: #344054;
}

.alt__badge--dubbing {
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  color: #6941c6;
}

.alt__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #475467;
  cursor: pointer;
}

.alt__icon-btn:hover {
  background: #f2f4f7;
}

.alt__icon-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.alt__no-actions {
  display: inline-block;
  width: 24px;
  height: 24px;
}

.alt__loading {
  padding: 8px 24px;
}

.alt__skeleton-row {
  display: grid;
  grid-template-columns: 2.6fr 1fr 0.6fr 0.8fr;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #eaecf0;
}

.alt__skeleton-row:last-child {
  border-bottom: none;
}

.alt__skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 9999px;
  background: #f2f4f7;
}

.alt__skeleton-bar--wide {
  width: 60%;
}

.alt__skeleton-bar--badge {
  width: 34%;
  justify-self: center;
}

.alt__skeleton-bar--short {
  width: 34%;
  justify-self: center;
}

.alt__skeleton-bar--icon {
  width: 24px;
  height: 24px;
  justify-self: center;
}

.alt__empty {
  padding: 40px 24px;
  text-align: center;
}

.alt__empty__title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.alt__empty__subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.alt__pagination {
  border-top: 1px solid #eaecf0;
}

.alt__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
